/**
 * Glucose target ranges, per v1's `_glucoseTargets`
 * (clinician/src/components/cgm/cgmUtils.ts:73). Five bands from very
 * low to very high; defaults come from clinical guidance and can be
 * overridden per patient via the app-data store.
 *
 * All bounds stored internally in mmol/L; converted to mg/dL at the
 * display layer when the patient prefers that unit.
 *
 * Color palette matches v1 — soft clinical CGM scheme: dark reddish-brown
 * for very low, muted red for low, soft green for target, pale yellow for
 * high, golden tan for very high.
 */
export type GlucoseTargetName = 'lowDanger' | 'low' | 'target' | 'high' | 'highDanger';

export type GlucoseUnit = 'mmol/L' | 'mg/dL';

export type GlucoseTarget = {
  mmolLower: number;
  mmolUpper: number;
  color: string;
  label: string;
};

export type GlucoseTargets = Record<GlucoseTargetName, GlucoseTarget>;

const MMOL_TO_MG_DL = 18.0182;

export const mmolToMgDl = (mmol: number): number => mmol * MMOL_TO_MG_DL;
export const mgDlToMmol = (mgDl: number): number => mgDl / MMOL_TO_MG_DL;

export const formatGlucose = (mmol: number, unit: GlucoseUnit): number => {
  if (unit === 'mmol/L') return Math.round(mmol * 10) / 10;
  return Math.round(mmolToMgDl(mmol));
};

/**
 * v1's defaults from `_glucoseTargets`. Always in mmol/L.
 */
export const DEFAULT_GLUCOSE_TARGETS: GlucoseTargets = {
  lowDanger: {
    mmolLower: 0,
    mmolUpper: 3.0,
    color: '#981E16', // dark reddish-brown
    label: 'Very Low',
  },
  low: {
    mmolLower: 3.0,
    mmolUpper: 3.9,
    color: '#DE6B6B', // muted red
    label: 'Low',
  },
  target: {
    mmolLower: 3.9,
    mmolUpper: 10.0,
    color: '#6BA436', // soft green
    label: 'Target',
  },
  high: {
    mmolLower: 10.0,
    mmolUpper: 13.9,
    color: '#F7CE5A', // pale yellow / light gold
    label: 'High',
  },
  highDanger: {
    mmolLower: 13.9,
    mmolUpper: 22.2, // visual cap; not a hard ceiling
    color: '#D5712A', // deeper golden/tan
    label: 'Very High',
  },
};

/**
 * Order matters for stacking: lowDanger at the bottom of the glucose axis,
 * highDanger at the top. UI components iterate this from bottom-up or
 * top-down as needed.
 */
export const TARGET_ORDER: GlucoseTargetName[] = [
  'lowDanger',
  'low',
  'target',
  'high',
  'highDanger',
];

/**
 * Wire-format used by v1 in app-data storage: just the upper bounds for
 * each named threshold (the lower bound of one range = upper of the
 * previous, so storing only uppers is unambiguous).
 *
 *   { app: {target: 10, ...}, api: {target: 10, ...}, default: {...} }
 *
 * For V2 we simplify to a single layer (`{target: 10, ...}`) and return a
 * fully-resolved `GlucoseTargets` object to consumers.
 */
export type StoredGlucoseRanges = Partial<Record<GlucoseTargetName, number>>;

/**
 * Merge stored upper-bound overrides into the default ranges. Each band's
 * `mmolUpper` is taken from the override when present; the next band's
 * `mmolLower` follows automatically so the bands stay contiguous.
 */
export const resolveGlucoseTargets = (overrides?: StoredGlucoseRanges | null): GlucoseTargets => {
  const out: GlucoseTargets = JSON.parse(JSON.stringify(DEFAULT_GLUCOSE_TARGETS));
  if (!overrides) return out;
  for (let i = 0; i < TARGET_ORDER.length; i++) {
    const name = TARGET_ORDER[i];
    const overrideUpper = overrides[name];
    if (typeof overrideUpper === 'number' && Number.isFinite(overrideUpper)) {
      out[name].mmolUpper = overrideUpper;
      const nextName = TARGET_ORDER[i + 1];
      if (nextName) out[nextName].mmolLower = overrideUpper;
    }
  }
  return out;
};

/**
 * Reverse of `resolveGlucoseTargets` — produce the wire-format payload
 * for a PUT to app-data storage. Only the upper bounds get stored.
 */
export const toStored = (targets: GlucoseTargets): StoredGlucoseRanges => {
  const out: StoredGlucoseRanges = {};
  for (const name of TARGET_ORDER) {
    out[name] = targets[name].mmolUpper;
  }
  return out;
};

/**
 * Resolve the patient's preferred display unit from their flag bag.
 * `patient_glucose_units_mmol_l === false` → mg/dL; otherwise mmol/L.
 */
export const glucoseUnitFor = (flags: Record<string, unknown> | undefined | null): GlucoseUnit =>
  flags?.patient_glucose_units_mmol_l === false ? 'mg/dL' : 'mmol/L';
